/**
 * Whether this module's edits have reached the server.
 *
 * Autosave means nobody presses Save in the ordinary case, so the state has
 * to be readable without one.
 *
 * @module components/studio/ModuleSaveStatus
 */

import React from "react";

import type { SaveStatus } from "@/components/studio/useModuleEditor";
import { Icon, Text } from "@/components/ui";

const COPY: Record<SaveStatus, string> = {
  idle: "Changes save automatically",
  saving: "Saving…",
  saved: "All module changes are saved",
  error: "The last save failed",
};

export function ModuleSaveStatus({
  status,
  dirty,
  canEdit,
}: {
  status: SaveStatus;
  dirty: boolean;
  /** A module nobody can edit here saves nothing, so it promises no autosave. */
  canEdit: boolean;
}): React.ReactElement {
  let appearance: { icon: string; className: string };
  if (status === "error") {
    appearance = { icon: "warning-circle", className: "text-error-strong" };
  } else if (dirty) {
    appearance = { icon: "clock", className: "text-warning-strong" };
  } else if (status === "saved") {
    appearance = { icon: "check-circle", className: "text-success-strong" };
  } else {
    appearance = { icon: "cloud-check", className: "text-muted" };
  }

  return (
    // A live region rather than bare text: with autosave, this is the only
    // thing that says an edit landed, and it changes without a press.
    <span
      role="status"
      data-testid="module-workspace-dirty-state"
      data-dirty={dirty ? "true" : "false"}
    >
      {!canEdit && !dirty && status === "idle" ? null : (
        <Text as="span" className={`inline-flex items-center gap-1 ${appearance.className}`}>
          <Icon name={appearance.icon} aria-hidden />
          {dirty && status !== "error" ? "This module has unsaved changes." : COPY[status]}
        </Text>
      )}
    </span>
  );
}

export default ModuleSaveStatus;
